import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import NavBar from "@/components/NavBar";
import {
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  BarChart3,
  Shield,
  Zap,
  Target,
  Star,
  Award,
  Globe,
} from "lucide-react";

const Index = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <NavBar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center max-w-5xl mx-auto">
            <div className="relative mb-12 animate-fade-in">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-full blur-2xl"></div>
              <Logo size="lg" />
              <div className="absolute -right-12 -top-8 transform rotate-12 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg">
                Enterprise Ready
              </div>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in leading-tight">
              Validate Product Ideas with
              <span className="text-gradient block mt-2">
                Data-Driven Insights
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-600 max-w-4xl mb-8 animate-fade-in leading-relaxed">
              The comprehensive product validation platform trusted by
              enterprises, startups, and innovation teams to make informed
              decisions before investing in development.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 mb-12 animate-fade-in">
              <Button
                size="lg"
                className="text-lg px-10 py-4 shadow-lg hover:shadow-xl transition-all duration-300"
                asChild
              >
                <Link to="/create">
                  Start Validation Campaign
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-10 py-4 border-2 hover:bg-slate-50 transition-all duration-300"
                asChild
              >
                <Link to="/browse">View Live Campaigns</Link>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-8 mb-16 text-slate-500">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-500" />
                <span className="font-medium">Enterprise Security</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-500" />
                <span className="font-medium">100+ Validated Ideas</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary-500" />
                <span className="font-medium">Global Reach</span>
              </div>
            </div>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 w-full max-w-4xl">
              {[
                {
                  icon: (
                    <BarChart3 className="w-8 h-8 mb-3 mx-auto text-primary-500" />
                  ),
                  label: "Advanced Analytics",
                  desc: "Real-time insights",
                },
                {
                  icon: (
                    <Target className="w-8 h-8 mb-3 mx-auto text-primary-500" />
                  ),
                  label: "Market Validation",
                  desc: "Precise targeting",
                },
                {
                  icon: (
                    <Zap className="w-8 h-8 mb-3 mx-auto text-primary-500" />
                  ),
                  label: "Rapid Deployment",
                  desc: "Launch in minutes",
                },
                {
                  icon: (
                    <Award className="w-8 h-8 mb-3 mx-auto text-primary-500" />
                  ),
                  label: "Expert Support",
                  desc: "24/7 assistance",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  {item.icon}
                  <h3 className="font-semibold text-slate-900 mb-1">
                    {item.label}
                  </h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Professional Process */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Proven Validation Methodology
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our systematic approach combines behavioral psychology, market
              research, and data analytics to deliver actionable insights for
              strategic decision-making.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {[
              {
                step: "01",
                title: "Strategic Campaign Design",
                description:
                  "Define your hypothesis, target demographics, and key performance indicators. Our platform guides you through best practices for maximum response rates and data quality.",
                icon: <Target className="w-12 h-12 text-primary-500" />,
              },
              {
                step: "02",
                title: "Multi-Channel Distribution",
                description:
                  "Deploy your campaign across multiple touchpoints with advanced targeting capabilities. Track engagement metrics and optimize reach in real-time.",
                icon: <TrendingUp className="w-12 h-12 text-primary-500" />,
              },
              {
                step: "03",
                title: "Actionable Intelligence",
                description:
                  "Access comprehensive analytics dashboards with predictive modeling, sentiment analysis, and strategic recommendations for informed decision-making.",
                icon: <BarChart3 className="w-12 h-12 text-primary-500" />,
              },
            ].map((item, index) => (
              <div
                key={index}
                className="relative bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white flex items-center justify-center font-bold text-lg">
                  {item.step}
                </div>
                <div className="mb-6 mt-4">{item.icon}</div>
                <h3 className="text-2xl font-semibold mb-4 text-slate-900">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Button size="lg" className="text-lg px-10 py-4 shadow-lg" asChild>
              <Link to="/create">Begin Validation Process</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Enterprise Features */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Enterprise-Grade Capabilities
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Built for scale with advanced features that meet the demands of
              modern product teams and innovation departments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[
              {
                title: "Advanced Analytics Suite",
                description:
                  "Real-time dashboards with predictive modeling, cohort analysis, and statistical significance testing.",
                icon: <BarChart3 className="w-8 h-8 text-primary-500" />,
              },
              {
                title: "Behavioral Insights Engine",
                description:
                  "Deep dive into user psychology with sentiment analysis, engagement patterns, and decision drivers.",
                icon: <Users className="w-8 h-8 text-primary-500" />,
              },
              {
                title: "Price Optimization Models",
                description:
                  "Van Westendorp analysis, conjoint analysis, and dynamic pricing recommendations.",
                icon: <TrendingUp className="w-8 h-8 text-primary-500" />,
              },
              {
                title: "Feature Prioritization Matrix",
                description:
                  "Data-driven feature ranking using Kano model, RICE framework, and user impact scoring.",
                icon: <Target className="w-8 h-8 text-primary-500" />,
              },
              {
                title: "Multi-Stakeholder Collaboration",
                description:
                  "Role-based access controls, approval workflows, and stakeholder communication tools.",
                icon: <Users className="w-8 h-8 text-primary-500" />,
              },
              {
                title: "API & Integration Hub",
                description:
                  "Seamless integration with CRM, product management, and business intelligence platforms.",
                icon: <Zap className="w-8 h-8 text-primary-500" />,
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="p-8 border border-slate-200 rounded-2xl bg-white hover:shadow-lg transition-all duration-300 group"
              >
                <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-slate-900">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Trusted by Innovation Leaders
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Join thousands of product teams who have accelerated their
              go-to-market strategy with data-driven validation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                metric: "98%",
                label: "Customer Satisfaction",
                icon: <Star className="w-8 h-8" />,
              },
              {
                metric: "Over $1.0M",
                label: "Saved in Dev Costs",
                icon: <TrendingUp className="w-8 h-8" />,
              },
              {
                metric: "45 Days",
                label: "Faster Time-to-Market",
                icon: <Zap className="w-8 h-8" />,
              },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 bg-white/10 rounded-2xl backdrop-blur-sm"
              >
                <div className="mb-4 flex justify-center">{stat.icon}</div>
                <div className="text-4xl font-bold mb-2">{stat.metric}</div>
                <div className="text-lg opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Product Strategy?
          </h2>
          <p className="text-xl text-slate-300 mb-12 max-w-3xl mx-auto">
            Join the ranks of successful product teams who validate before they
            build. Start your first campaign today and make decisions with
            confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button
              size="lg"
              className="text-lg px-10 py-4 bg-primary-500 hover:bg-primary-600 shadow-lg"
              asChild
            >
              <Link to="/create">Start Free Campaign</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-10 py-4 border-2 border-slate-300 text-slate-900 bg-white hover:bg-slate-50"
              asChild
            >
              <Link to="/how-it-works">Learn More</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start mb-12">
            <div className="mb-8 md:mb-0">
              <Logo />
              <p className="mt-4 text-slate-600 max-w-md">
                The professional product validation platform for modern teams.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Product</h4>
                <div className="space-y-2">
                  <Link
                    to="/browse"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Browse Campaigns
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    How It Works
                  </Link>
                  <Link
                    to="/create"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Get Started
                  </Link>
                </div>
              </div>
              {/* <div>
                <h4 className="font-semibold text-slate-900 mb-4">Company</h4>
                <div className="space-y-2">
                  <a
                    href="#"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    About
                  </a>
                  <a
                    href="#"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Careers
                  </a>
                  <a
                    href="#"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Press
                  </a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Resources</h4>
                <div className="space-y-2">
                  <a
                    href="#"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Documentation
                  </a>
                  <a
                    href="#"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    API Reference
                  </a>
                  <a
                    href="#"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Best Practices
                  </a>
                </div>
              </div> */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Support</h4>
                <div className="space-y-2">
                  <a
                    href="/help"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Help & Support
                  </a>
                  <a
                    href="/privacy"
                    className="block text-slate-600 hover:text-primary-600 transition-colors"
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8 text-center text-slate-600">
            <p>
              &copy; {new Date().getFullYear()} FundORFling. All rights
              reserved. Built for professional product teams.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
